import apiConfig from './apiConfig';

class ScheduleService {
  constructor() {
    this.baseURL = apiConfig.SCHEDULES.BASE;
  }

  // Buscar agendamentos
  async getAppointments(params = {}) {
    try {
      const response = await fetch(`${this.baseURL}/appointments/?${new URLSearchParams(params)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar agendamentos:', error);
      throw error;
    }
  }

  // Buscar agendamento por ID
  async getAppointmentById(id) {
    try {
      const response = await fetch(`${this.baseURL}/appointments/${id}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar agendamento:', error);
      throw error;
    }
  }

  // Buscar agendamento por número de acesso (accession_number)
  async getAppointmentByAccessionNumber(accessionNumber) {
    try {
      // Usar o endpoint do MedicalReports que é específico para accession_number
      const response = await fetch(`/api/v1/medical-reports/agendamentos/por-accession/${accessionNumber}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erro na API de agendamento:', errorData);
        throw new Error(`Erro na requisição: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar agendamento por accession_number:', error);
      throw error;
    }
  }

  // Criar novo agendamento (para reconvocação)
  async createAppointment(appointmentData, scheduleId) {
    try {
      const response = await fetch(`${this.baseURL}/${scheduleId}/appointments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(appointmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erro na requisição: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
      throw error;
    }
  }

  // Atualizar agendamento (para marcar como reconvocado)
  async updateAppointment(id, appointmentData, scheduleId) {
    try {
      const response = await fetch(`${this.baseURL}/${scheduleId}/appointments/${id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(appointmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erro na requisição: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao atualizar agendamento:', error);
      throw error;
    }
  }

  // Registrar auditoria da reconvocação
  async registerRecallAudit(auditData) {
    try {
      const response = await fetch('/api/v1/audit/register/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(auditData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erro na requisição: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao registrar auditoria:', error);
      throw error;
    }
  }

  // Reconvocar paciente (função principal)
  async recallPatient(originalAppointment, recallData) {
    try {
      // 1. Criar novo agendamento para reconvocação
      const newAppointmentData = {
        patient_id: originalAppointment.patient.id,
        service_id: originalAppointment.service.id,
        date: recallData.new_date,
        time: recallData.new_time,
        status: 'pending',
        motivo_marcacao: recallData.reason,
        procedencia: 'Reconvocação',
        is_inpatient: false,
        is_urgent: false,
        send_preparation_email: false,
        send_preparation_sms: false,
        notes: recallData.reason
      };

      // Para criar o agendamento, precisamos do schedule_id
      // Como o endpoint do MedicalReports não retorna isso, vamos usar um schedule padrão
      // ou buscar o schedule_id através de outro endpoint
      const scheduleId = 1; // TODO: Implementar busca do schedule_id correto
      
      const newAppointment = await this.createAppointment(newAppointmentData, scheduleId);

      // 2. Atualizar agendamento original para status "recalled"
      const updatedOriginalAppointment = {
        ...originalAppointment,
        status: 'recalled',
        notes: recallData.reason
      };

      await this.updateAppointment(originalAppointment.id, updatedOriginalAppointment, scheduleId);

      // 3. Registrar auditoria
      const auditData = {
        action: 'patient_recall',
        details: {
          original_appointment_id: originalAppointment.id,
          new_appointment_id: newAppointment.id,
          patient_name: originalAppointment.patient.name,
          original_date: originalAppointment.date,
          original_time: originalAppointment.time,
          new_date: recallData.new_date,
          new_time: recallData.new_time,
          reason: recallData.reason,
          user: localStorage.getItem('user_name') || 'Sistema'
        }
      };

      await this.registerRecallAudit(auditData);

      return {
        success: true,
        newAppointment,
        originalAppointment: updatedOriginalAppointment
      };

    } catch (error) {
      console.error('Erro ao reconvocar paciente:', error);
      throw error;
    }
  }

  // Buscar horários disponíveis
  async getAvailableTimes(date, scheduleId = null) {
    try {
      // Se não temos scheduleId, retornar horários padrão
      if (!scheduleId) {
        console.warn('Schedule ID não fornecido, usando horários padrão');
        return [
          '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
          '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
        ];
      }

      const response = await fetch(`${this.baseURL}/${scheduleId}/available-times/?date=${date}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erro na API de horários disponíveis:', errorData);
        throw new Error(`Erro na requisição: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      return data.times || [];
    } catch (error) {
      console.error('Erro ao buscar horários disponíveis:', error);
      // Retornar horários padrão em caso de erro
      return [
        '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
        '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
      ];
    }
  }
}

export default new ScheduleService(); 